/**
 * room/mood.ts — Room Mood & Atmosphere
 *
 * A room is never neutral. Every lamp, every potted fern, every open window
 * at golden hour contributes to a feeling. This module computes that feeling
 * precisely — and then translates it into the things players actually
 * experience: colored light, ambient sound, the way their character slouches
 * or perks up when they walk in.
 *
 * The Aeterna reads room mood to decide what to say. Players feel it without
 * being told. That's the goal.
 */

// ─── Core Mood State ─────────────────────────────────────────────────────────

/**
 * The emotional fingerprint of a room at a moment in time.
 *
 * All dimensions are continuous so mood can blend and drift.
 * Furniture, visitors, time-of-day, and weather all push and pull these
 * values — the result is the room's felt character.
 */
export interface RoomMood {
  /** -1 = cold and clinical  →  +1 = warm and fire-lit */
  warmth: number;
  /** 0 = sparse, open, minimal  →  1 = nest-like, layered, sheltering */
  coziness: number;
  /** -1 = drowsy, hushed  →  +1 = lively, buzzing */
  energy: number;
  /** 0 = ordinary, transparent  →  1 = enchanting, arcane, storied */
  mystery: number;
  /** 0 = sterile, urban  →  1 = garden, verdant, alive */
  nature: number;
  /** 0 = dim, candlelit cave  →  1 = flooded with daylight */
  brightness: number;
  /** 0 = silent, still  →  1 = cacophonous, crowded */
  noise: number;
}

// ─── Mood Contribution ───────────────────────────────────────────────────────

/**
 * Per-furniture mood contribution. Exported so furniture.ts can reference it
 * without a circular dependency.
 */
export interface MoodContribution {
  warmth: number;    // -0.5 to +0.5
  coziness: number;  // 0 to +0.5
  energy: number;    // -0.5 to +0.5
  mystery: number;   // 0 to +0.5
  nature: number;    // 0 to +0.5
}

// ─── Lighting Output ─────────────────────────────────────────────────────────

export interface MoodLighting {
  /** CSS hex or rgba string for the ambient fill color */
  ambient: string;
  /** CSS color applied as a global compositing tint over the scene */
  tint: string;
  /** 0-1 alpha for the drop shadows — higher = more dramatic */
  shadowAlpha: number;
}

// ─── Ambience Output ─────────────────────────────────────────────────────────

export interface MoodAmbience {
  /** Sound effect / ambient track identifiers the audio layer should play */
  sounds: string[];
  /** Master volume for this ambient layer, 0-1 */
  volume: number;
}

// ─── Character Modifier Output ────────────────────────────────────────────────

export interface CharacterMoodModifier {
  /**
   * Energy modifier applied to character idle/walk animations.
   * Positive = more bounce, more frequent idle fidgets.
   * Negative = slower blinks, heavier footfall.
   */
  energyBias: number;
  /**
   * Sociability modifier: how likely a character is to initiate a wave,
   * sit down, or approach another character.
   */
  sociabilityBias: number;
  /**
   * Curiosity modifier: how likely a character is to walk toward
   * interactable furniture (bookshelf, Aeterna device, window).
   */
  curiosityBias: number;
  /**
   * Walk speed multiplier. Cozy, warm rooms slow you down a bit.
   * Lively, energetic spaces make you stride.
   */
  walkSpeedMult: number;
}

// ─── Time-of-Day Helpers ─────────────────────────────────────────────────────

/**
 * Named time bands that affect mood and lighting.
 * timeOfDay is 0-24 (floating point hours).
 */
type TimeBand =
  | "night"        // 0-6
  | "morning"      // 6-10
  | "midday"       // 10-16
  | "afternoon"    // 16-19
  | "evening"      // 19-22
  | "latenight";   // 22-24

function getTimeBand(timeOfDay: number): TimeBand {
  const h = ((timeOfDay % 24) + 24) % 24;
  if (h < 6)  return "night";
  if (h < 10) return "morning";
  if (h < 16) return "midday";
  if (h < 19) return "afternoon";
  if (h < 22) return "evening";
  return "latenight";
}

/**
 * Smooth interpolation between two values using a cubic ease.
 * Used to prevent hard transitions at time band boundaries.
 */
function smoothstep(a: number, b: number, t: number): number {
  const c = Math.max(0, Math.min(1, t));
  return a + (b - a) * (c * c * (3 - 2 * c));
}

/** Clamp to [-1, 1] */
function clamp11(v: number): number {
  return Math.max(-1, Math.min(1, v));
}

/** Clamp to [0, 1] */
function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

// ─── Minimal Room Shape (avoid circular imports) ─────────────────────────────

/**
 * Minimal interface for what computeRoomMood needs from a Room.
 * The full Room type lives in room.ts; we only import what we need here
 * to stay inside our module boundary.
 */
export interface RoomMoodInput {
  furniture: Array<{
    state: Record<string, unknown>;
    moodContribution?: MoodContribution;
    lightEmission?: { intensity: number };
    soundEmission?: { volume: number };
  }>;
  lighting: { brightness: number };
  timeOfDay: number;
  weather: "clear" | "rain" | "snow" | "fog";
  visitors: string[];
  maxVisitors: number;
}

// ─── Core Computation ────────────────────────────────────────────────────────

/**
 * Compute the full RoomMood for a given room snapshot.
 *
 * The algorithm:
 *  1. Start from a baseline neutral mood.
 *  2. Sum all furniture mood contributions.
 *  3. Apply time-of-day modulation.
 *  4. Apply weather modulation.
 *  5. Apply visitor count modulation.
 *  6. Clamp everything to valid ranges.
 *
 * This function is called at most once per "mood tick" (not every frame).
 * Room updates should cache this result and recompute only when furniture
 * changes or a significant time interval passes.
 */
export function computeRoomMood(room: RoomMoodInput): RoomMood {
  // ── 1. Neutral baseline ──────────────────────────────────────────────────
  let warmth   = 0;
  let coziness = 0;
  let energy   = 0;
  let mystery  = 0;
  let nature   = 0;
  let brightness = room.lighting.brightness;
  let noise    = 0;

  // ── 2. Furniture contributions ───────────────────────────────────────────
  for (const f of room.furniture) {
    const mc = f.moodContribution;
    if (!mc) continue;

    warmth   += mc.warmth;
    coziness += mc.coziness;
    energy   += mc.energy;
    mystery  += mc.mystery;
    nature   += mc.nature;

    // Active light sources boost brightness and warmth
    if (f.lightEmission && f.state.on !== false) {
      brightness += f.lightEmission.intensity * 0.15;
      warmth     += f.lightEmission.intensity * 0.05;
    }

    // Active sound sources increase noise
    if (f.soundEmission && f.state.on !== false) {
      noise += f.soundEmission.volume * 0.2;
    }
  }

  // Normalize furniture contributions — diminishing returns over 6 pieces
  const furnitureCount = room.furniture.length;
  const furnitureScale = furnitureCount > 0
    ? Math.min(1, 6 / furnitureCount) * 0.8 + 0.2
    : 1;
  warmth   *= furnitureScale;
  coziness *= furnitureScale;
  energy   *= furnitureScale;
  mystery  *= furnitureScale;
  nature   *= furnitureScale;

  // ── 3. Time of day modulation ────────────────────────────────────────────
  const band = getTimeBand(room.timeOfDay);
  const h = ((room.timeOfDay % 24) + 24) % 24;

  switch (band) {
    case "morning": {
      // 6-10: warm, gentle energy, birds
      const progress = (h - 6) / 4; // 0 at 6am, 1 at 10am
      warmth     += smoothstep(0.1, 0.25, progress);
      energy     += smoothstep(-0.1, 0.2, progress);
      brightness += smoothstep(0.2, 0.5, progress);
      break;
    }
    case "midday": {
      // 10-16: neutral, bright, clear
      brightness += 0.5;
      energy     += 0.1;
      break;
    }
    case "afternoon": {
      // 16-19: golden hour — warm, rich
      const progress = (h - 16) / 3; // 0 at 4pm, 1 at 7pm
      warmth     += smoothstep(0.2, 0.4, progress);
      brightness += smoothstep(0.4, 0.2, progress); // dimming
      coziness   += smoothstep(0, 0.15, progress);
      break;
    }
    case "evening": {
      // 19-22: lamps matter, cozy settling
      const progress = (h - 19) / 3;
      warmth     += 0.2;
      coziness   += smoothstep(0.1, 0.3, progress);
      brightness -= smoothstep(0, 0.3, progress);
      energy     -= smoothstep(0, 0.2, progress);
      break;
    }
    case "latenight": {
      // 22-24: winding down, intimate, dim
      warmth     += 0.1;
      coziness   += 0.2;
      brightness -= 0.4;
      energy     -= 0.3;
      mystery    += 0.15;
      break;
    }
    case "night": {
      // 0-6: deep night, mysterious, very dim
      const progress = h / 6; // 0 at midnight, 1 at 6am
      brightness -= smoothstep(0.5, 0.2, progress);
      energy     -= smoothstep(0.4, 0.1, progress);
      mystery    += smoothstep(0.2, 0.05, progress);
      warmth     -= 0.1;
      break;
    }
  }

  // ── 4. Weather modulation ────────────────────────────────────────────────
  switch (room.weather) {
    case "rain":
      noise      += 0.15; // pattering
      coziness   += 0.1;  // shelter effect
      mystery    += 0.05;
      brightness -= 0.15;
      break;
    case "snow":
      brightness += 0.1;  // reflected light
      coziness   += 0.15;
      warmth     -= 0.05;
      energy     -= 0.1;
      break;
    case "fog":
      brightness -= 0.2;
      mystery    += 0.2;
      energy     -= 0.15;
      break;
    case "clear":
      // no adjustment — clear is the baseline
      break;
  }

  // ── 5. Visitor count modulation ──────────────────────────────────────────
  const visitorCount = room.visitors.length;
  if (visitorCount === 0) {
    // Empty room: quiet, still, slightly wistful
    noise  -= 0.1;
    energy -= 0.15;
  } else if (visitorCount <= 3) {
    // Intimate: warmth, gentle energy
    warmth += 0.05;
    energy += 0.1;
    noise  += 0.05;
  } else if (visitorCount <= 8) {
    // Social: lively
    energy += 0.25;
    noise  += 0.2;
    warmth += 0.1;
  } else {
    // Bustling: loud, energetic, less intimate
    energy   += 0.4;
    noise    += 0.45;
    coziness -= 0.15;
  }

  // ── 6. Clamp all dimensions ──────────────────────────────────────────────
  return {
    warmth:     clamp11(warmth),
    coziness:   clamp01(coziness),
    energy:     clamp11(energy),
    mystery:    clamp01(mystery),
    nature:     clamp01(nature),
    brightness: clamp01(brightness),
    noise:      clamp01(noise),
  };
}

// ─── Mood Description ────────────────────────────────────────────────────────

/**
 * Produce a human-readable description of the room's felt character.
 *
 * Used by the Aeterna when making contextual comments, and shown
 * to players in the room info panel.
 *
 * Examples:
 *   "a warm, cozy nook"
 *   "a bright, lively social space"
 *   "a mysterious, dim study"
 *   "a serene garden room"
 */
export function getMoodDescription(mood: RoomMood): string {
  const parts: string[] = [];

  // ── Warmth ───────────────────────────────────────────────────────────────
  if (mood.warmth > 0.5)       parts.push("warm");
  else if (mood.warmth > 0.2)  parts.push("comfortable");
  else if (mood.warmth < -0.4) parts.push("cool and crisp");
  else if (mood.warmth < -0.1) parts.push("airy");

  // ── Brightness ───────────────────────────────────────────────────────────
  if (mood.brightness > 0.75)      parts.push("sun-drenched");
  else if (mood.brightness > 0.55) parts.push("bright");
  else if (mood.brightness < 0.2)  parts.push("dim");
  else if (mood.brightness < 0.35) parts.push("softly lit");

  // ── Coziness ─────────────────────────────────────────────────────────────
  if (mood.coziness > 0.7)      parts.push("cozy");
  else if (mood.coziness > 0.5) parts.push("inviting");
  else if (mood.coziness < 0.1) parts.push("sparse");

  // ── Energy ───────────────────────────────────────────────────────────────
  if (mood.energy > 0.5)       parts.push("lively");
  else if (mood.energy > 0.25) parts.push("sociable");
  else if (mood.energy < -0.4) parts.push("hushed");
  else if (mood.energy < -0.15) parts.push("quiet");

  // ── Mystery ──────────────────────────────────────────────────────────────
  if (mood.mystery > 0.6)      parts.push("mysterious");
  else if (mood.mystery > 0.35) parts.push("enchanting");
  else if (mood.mystery > 0.2)  parts.push("intriguing");

  // ── Nature ───────────────────────────────────────────────────────────────
  if (mood.nature > 0.7)       parts.push("verdant");
  else if (mood.nature > 0.45) parts.push("garden-like");
  else if (mood.nature > 0.25) parts.push("touched by nature");

  // ── Noise ────────────────────────────────────────────────────────────────
  if (mood.noise > 0.7)        parts.push("bustling");
  else if (mood.noise < 0.05)  parts.push("still");

  if (parts.length === 0) return "an ordinary room";

  // Compose: "a warm, cozy, lively space"
  const tail = parts.length > 1
    ? parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1]
    : parts[0];

  // Choose the right article
  const startsWithVowel = /^[aeiou]/i.test(tail);
  const article = startsWithVowel ? "an" : "a";

  // Add a room noun based on dominant traits
  let noun = "space";
  if (mood.coziness > 0.6 && mood.mystery < 0.3) noun = "nook";
  else if (mood.nature > 0.5) noun = "garden room";
  else if (mood.mystery > 0.5) noun = "study";
  else if (mood.energy > 0.5) noun = "social space";
  else if (mood.brightness > 0.7) noun = "airy room";
  else if (mood.coziness > 0.4) noun = "retreat";

  return `${article} ${tail} ${noun}`;
}

// ─── Mood Lighting ───────────────────────────────────────────────────────────

/**
 * Derive the ambient lighting character from mood and time of day.
 *
 * Returns:
 *   ambient — base fill color for the room (very lightly applied)
 *   tint    — color multiplied over the entire scene (compositing layer)
 *   shadowAlpha — how dark and present the drop shadows are
 *
 * All colors are CSS rgba() strings.
 */
export function getMoodLighting(mood: RoomMood, timeOfDay: number): MoodLighting {
  const band = getTimeBand(timeOfDay);
  const h = ((timeOfDay % 24) + 24) % 24;

  // ── Base ambient color ───────────────────────────────────────────────────
  // Neutral ambient: a warm off-white. We then shift it toward time color.
  let ambR = 248, ambG = 243, ambB = 235; // warm paper white

  switch (band) {
    case "morning": {
      const t = (h - 6) / 4;
      // Dawn: pink-orange → warm yellow
      ambR = Math.round(smoothstep(255, 255, t));
      ambG = Math.round(smoothstep(200, 240, t));
      ambB = Math.round(smoothstep(180, 200, t));
      break;
    }
    case "midday":
      ambR = 252; ambG = 248; ambB = 240; // near-white with warmth
      break;
    case "afternoon": {
      const t = (h - 16) / 3;
      // Bright → golden orange
      ambR = Math.round(smoothstep(252, 255, t));
      ambG = Math.round(smoothstep(240, 210, t));
      ambB = Math.round(smoothstep(220, 150, t));
      break;
    }
    case "evening": {
      const t = (h - 19) / 3;
      ambR = Math.round(smoothstep(255, 200, t));
      ambG = Math.round(smoothstep(210, 160, t));
      ambB = Math.round(smoothstep(150, 120, t));
      break;
    }
    case "latenight":
      ambR = 150; ambG = 130; ambB = 160; // cool purple dusk
      break;
    case "night": {
      const t = h / 6;
      ambR = Math.round(smoothstep(100, 160, t));
      ambG = Math.round(smoothstep(90,  120, t));
      ambB = Math.round(smoothstep(130, 155, t));
      break;
    }
  }

  // Warmth shifts ambient toward amber
  if (mood.warmth > 0) {
    ambR = Math.min(255, ambR + Math.round(mood.warmth * 15));
    ambG = Math.min(255, ambG + Math.round(mood.warmth * 6));
    ambB = Math.max(0,   ambB - Math.round(mood.warmth * 20));
  } else {
    // Cool rooms shift blue
    ambB = Math.min(255, ambB + Math.round(-mood.warmth * 20));
    ambR = Math.max(0,   ambR + Math.round(mood.warmth * 10));
  }

  const brightnessFactor = clamp01(mood.brightness);
  const ambient = `rgba(${ambR},${ambG},${ambB},${(0.06 + brightnessFactor * 0.08).toFixed(3)})`;

  // ── Tint color ───────────────────────────────────────────────────────────
  // A very subtle color wash applied over the whole scene.
  let tintR = ambR, tintG = ambG, tintB = ambB;
  let tintA = 0;

  switch (band) {
    case "morning":
      tintA = 0.06;
      break;
    case "afternoon":
      tintR = 255; tintG = 185; tintB = 100;
      tintA = 0.10;
      break;
    case "evening":
      tintR = 220; tintG = 150; tintB = 100;
      tintA = 0.12;
      break;
    case "latenight":
      tintR = 100; tintG = 80; tintB = 140;
      tintA = 0.15;
      break;
    case "night":
      tintR = 60; tintG = 50; tintB = 100;
      tintA = 0.20;
      break;
    case "midday":
      tintA = 0.0;
      break;
  }

  // Mystery adds a slight purple tint
  if (mood.mystery > 0.3) {
    tintR = Math.round(tintR * 0.95 + 80 * 0.05 * mood.mystery);
    tintB = Math.min(255, tintB + Math.round(mood.mystery * 20));
    tintA += mood.mystery * 0.04;
  }

  const tint = `rgba(${tintR},${tintG},${tintB},${clamp01(tintA).toFixed(3)})`;

  // ── Shadow alpha ─────────────────────────────────────────────────────────
  // Brighter rooms have lighter, shorter shadows.
  // Evening / warm rooms have soft orange-tinged shadows (handled by tint).
  const shadowAlpha = clamp01(
    0.35 - mood.brightness * 0.2 + (mood.mystery > 0.4 ? 0.1 : 0)
  );

  return { ambient, tint, shadowAlpha };
}

// ─── Mood Ambience ───────────────────────────────────────────────────────────

/**
 * Derive ambient sound identifiers from room mood.
 *
 * The audio layer will look up each string in its sound registry.
 * Volume is a 0-1 multiplier on the ambient bus.
 *
 * Sound identifier conventions:
 *   "amb_*"   — looping ambient beds
 *   "sfx_*"   — occasional one-shot sounds (wind, birds)
 *   "music_*" — musical background (only when mood warrants)
 */
export function getMoodAmbience(mood: RoomMood): MoodAmbience {
  const sounds: string[] = [];

  // ── Nature sounds ─────────────────────────────────────────────────────────
  if (mood.nature > 0.5) sounds.push("amb_garden");
  else if (mood.nature > 0.25) sounds.push("sfx_leaves");

  // ── Noise / energy ────────────────────────────────────────────────────────
  if (mood.noise > 0.6) sounds.push("amb_crowd_murmur");
  else if (mood.noise > 0.35) sounds.push("amb_gentle_chatter");

  // ── Warmth / fire ─────────────────────────────────────────────────────────
  if (mood.warmth > 0.5) sounds.push("sfx_fire_crackle");

  // ── Mystery ───────────────────────────────────────────────────────────────
  if (mood.mystery > 0.5) sounds.push("amb_mystery_drone");
  else if (mood.mystery > 0.3) sounds.push("amb_soft_hum");

  // ── Base ambience based on energy ─────────────────────────────────────────
  if (mood.energy > 0.4) {
    sounds.push("amb_active_room");
  } else if (mood.energy < -0.3) {
    sounds.push("amb_still_room");
  } else {
    sounds.push("amb_neutral_room");
  }

  // ── Coziness ─────────────────────────────────────────────────────────────
  if (mood.coziness > 0.6 && mood.noise < 0.3) {
    sounds.push("amb_cozy_hearth");
  }

  // Volume: quieter rooms play ambience more softly so it doesn't intrude
  const volume = clamp01(0.3 + mood.brightness * 0.15 + mood.noise * 0.25);

  return { sounds, volume };
}

/**
 * Get ambient sounds for a specific zone type.
 * These are layered on top of mood-based ambience to give each zone
 * its distinctive audio character.
 */
export function getZoneAmbience(zoneType: string): string[] {
  switch (zoneType) {
    case "forest":
    case "nature":    return ["amb_forest"];
    case "underground": return ["amb_underground"];
    case "harbor":    return ["amb_harbor"];
    case "mountain":
    case "skyline":   return ["amb_mountain"];
    case "commercial":
    case "social":    return ["amb_town"];
    case "danger":    return ["amb_danger"];
    default:          return [];
  }
}

// ─── Character Behavior Modifiers ────────────────────────────────────────────

/**
 * How does the room's mood change character behavior?
 *
 * These modifiers are hints to the character AI / animation layer.
 * They don't override player intent — they color the idle state,
 * movement style, and spontaneous social gestures.
 */
export function moodAffectsCharacter(mood: RoomMood): CharacterMoodModifier {
  // Energy bias: energetic rooms make characters move faster and gesture more
  const energyBias = mood.energy * 0.4 + mood.warmth * 0.1;

  // Sociability: warm, lively, noisy rooms make characters more social
  const sociabilityBias =
    mood.warmth * 0.3 + mood.energy * 0.2 + mood.coziness * 0.15 - mood.mystery * 0.1;

  // Curiosity: mysterious rooms make characters investigate furniture more
  const curiosityBias = mood.mystery * 0.4 + mood.nature * 0.15;

  // Walk speed: cozy/sleepy rooms = slower; lively rooms = faster
  const walkSpeedMult = clamp01(0.85 + mood.energy * 0.25 - mood.coziness * 0.1);

  return {
    energyBias:      clamp11(energyBias),
    sociabilityBias: clamp11(sociabilityBias),
    curiosityBias:   clamp01(curiosityBias),
    walkSpeedMult:   Math.max(0.5, Math.min(1.5, walkSpeedMult)),
  };
}

// ─── Aeterna Mood Context ────────────────────────────────────────────────────

/**
 * Produce a short structured context string for the Aeterna.
 * The Aeterna system prompt can embed this to ground contextual commentary.
 *
 * Format is deliberately terse — the Aeterna will interpret it, not
 * display it literally.
 */
export function getMoodAeternaContext(mood: RoomMood, timeOfDay: number): string {
  const band = getTimeBand(timeOfDay);
  const desc = getMoodDescription(mood);

  const traits: string[] = [];
  if (mood.warmth > 0.3)    traits.push("warm");
  if (mood.warmth < -0.2)   traits.push("cool");
  if (mood.coziness > 0.5)  traits.push("cozy");
  if (mood.energy > 0.4)    traits.push("lively");
  if (mood.energy < -0.3)   traits.push("sleepy");
  if (mood.mystery > 0.4)   traits.push("mysterious");
  if (mood.nature > 0.4)    traits.push("natural");
  if (mood.brightness < 0.3) traits.push("dim");
  if (mood.noise > 0.5)     traits.push("noisy");

  return [
    `Room: ${desc}`,
    `Time: ${band} (${Math.floor(timeOfDay)}:${String(Math.round((timeOfDay % 1) * 60)).padStart(2, "0")})`,
    traits.length > 0 ? `Traits: ${traits.join(", ")}` : "",
  ].filter(Boolean).join(" | ");
}
